/**
 * Pure cross-form summary-card resolver. No React, no Supabase — unit-tested in
 * isolation (tests/unit/summary-card-resolver.test.ts).
 *
 * Implements the resolution algorithm from
 * guide_documents/app_overview_documents/08_summary_card.md §8.4:
 * for each configured summary field, pick the right submission of its source
 * form (by aggregation strategy), pull field_values[source_field_key], format
 * it, and fall back to fallback_text when empty.
 */

export interface SummaryFieldConfig {
  id: string
  label: string
  source_form_id: string
  source_field_key: string
  /** latest_instance | first_instance | count | single */
  aggregation: string
  show_last_updated: boolean
  show_warning_indicator: boolean
  stale_threshold_days: number | null
  icon: string | null
  fallback_text: string | null
  /** Freeform: string template containing "{value}", or null. */
  format_hint: unknown
}

export interface SummarySubmission {
  id: string
  form_id: string
  instance_index: number
  field_values: Record<string, unknown>
  updated_at: string
  soft_warning_flags: unknown
}

export interface SummaryCardItem {
  id: string
  label: string
  /** Formatted value, or the fallback text when empty. */
  value: string
  isEmpty: boolean
  icon: string | null
  /** ISO timestamp of the resolved submission, or null. */
  lastUpdated: string | null
  isStale: boolean
  hasWarning: boolean
}

const DEFAULT_FALLBACK = '—'

/** Stringify a stored field value into a human label (Persian-first). */
function stringifyValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return ''
  if (Array.isArray(v)) {
    return v.map((x) => stringifyValue(x)).filter(Boolean).join('، ')
  }
  if (typeof v === 'object') {
    const r = v as Record<string, unknown>
    if (typeof r.originalName === 'string') return r.originalName
    if (typeof r.fa === 'string') return r.fa
    return JSON.stringify(v)
  }
  if (typeof v === 'boolean') return v ? 'بله' : 'خیر'
  return String(v)
}

/**
 * Apply the (freeform) format_hint. v1 supports a string template with a
 * "{value}" placeholder (e.g. "{value} kg"); anything else falls through to the
 * plain stringified value. Unknown shapes never throw — display resilience.
 */
function formatValue(v: unknown, hint: unknown): string {
  const base = stringifyValue(v)
  if (!base) return base
  if (typeof hint === 'string' && hint.includes('{value}')) {
    return hint.replace('{value}', base)
  }
  return base
}

function isStale(lastUpdated: string | null, thresholdDays: number | null): boolean {
  if (!lastUpdated || !thresholdDays || thresholdDays <= 0) return false
  const ageMs = Date.now() - new Date(lastUpdated).getTime()
  return ageMs > thresholdDays * 24 * 60 * 60 * 1000
}

function fieldHasWarning(sub: SummarySubmission | undefined, fieldKey: string): boolean {
  if (!sub) return false
  const flags = sub.soft_warning_flags
  if (!Array.isArray(flags)) return false
  return flags.some(
    (f) => f && typeof f === 'object' && (f as Record<string, unknown>).field_key === fieldKey,
  )
}

/** Pick the submission an aggregation strategy resolves to (excluding 'count'). */
function pickSubmission(
  subs: SummarySubmission[],
  aggregation: string,
): SummarySubmission | undefined {
  if (subs.length === 0) return undefined
  const sorted = [...subs].sort((a, b) => a.instance_index - b.instance_index)
  if (aggregation === 'first_instance') return sorted[0]
  // latest_instance | single | unknown → most recent instance
  return sorted[sorted.length - 1]
}

export function resolveSummaryValues(
  config: SummaryFieldConfig[],
  submissions: SummarySubmission[],
): SummaryCardItem[] {
  const byForm = new Map<string, SummarySubmission[]>()
  for (const s of submissions) {
    const list = byForm.get(s.form_id)
    if (list) list.push(s)
    else byForm.set(s.form_id, [s])
  }

  return [...config].map((sf) => {
    const subs = byForm.get(sf.source_form_id) ?? []

    if (sf.aggregation === 'count') {
      const lastUpdated = sf.show_last_updated && subs.length > 0
        ? subs.reduce((max, s) => (s.updated_at > max ? s.updated_at : max), subs[0].updated_at)
        : null
      return {
        id: sf.id,
        label: sf.label,
        value: subs.length.toLocaleString('fa-IR'),
        isEmpty: subs.length === 0,
        icon: sf.icon,
        lastUpdated,
        isStale: isStale(lastUpdated, sf.stale_threshold_days),
        hasWarning: false,
      }
    }

    const sub = pickSubmission(subs, sf.aggregation)
    const raw = sub?.field_values?.[sf.source_field_key]
    const formatted = formatValue(raw, sf.format_hint)
    const empty = formatted === ''
    const lastUpdated = sf.show_last_updated && sub ? sub.updated_at : null

    return {
      id: sf.id,
      label: sf.label,
      value: empty ? (sf.fallback_text || DEFAULT_FALLBACK) : formatted,
      isEmpty: empty,
      icon: sf.icon,
      lastUpdated,
      isStale: isStale(lastUpdated, sf.stale_threshold_days),
      hasWarning: sf.show_warning_indicator && fieldHasWarning(sub, sf.source_field_key),
    }
  })
}
